import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import {
  ClosedMultipleQuestionView,
  RenderChildrenAt,
} from "../components/Questions/ClosedMultipleQuestionView";
import { PatientPanelSearch } from "../components/search/patientPanelSearch";
import { QualifiedFeature, QualifiedFeatureFragment } from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PanelMultiSelectWithSearchProps {
  suggested: QualifiedFeature[];
  selected: QualifiedFeature[];
  onDone?: (selectedFeatures: QualifiedFeature[]) => void;
  // If set to true feature description will hide for all options even if it's present
  hideDescription?: boolean;
  questionText: string;
  miniView?: boolean;
  // Provides an option to change "None of the above" text
  skipButtonOverrideText?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const initializeOptions = (
  suggested: QualifiedFeature[],
  selected: QualifiedFeature[],
) => {
  const newOptions = new Map();
  suggested.forEach((suggested) =>
    newOptions.set(suggested.feature.id, [
      suggested,
      selected?.some((qf) => qf.id === suggested.feature.id) || false,
    ]),
  );
  return newOptions;
};

export const PanelMultiSelectWithSearch = ({
  selected,
  suggested,
  onDone,
  questionText,
  ...props
}: PanelMultiSelectWithSearchProps) => {
  const [options, setOptions] = useState<
    Map<string, [QualifiedFeature, boolean]>
  >(initializeOptions(suggested, selected));

  const handleDoneClicked = useCallback(() => {
    // Gather all selected ones
    onDone(
      Array.from(options.values())
        .filter(([feature, selected]) => selected)
        .map(([feature]) => feature),
    );
  }, [onDone, options]);

  const handleSuggestionToggle = useCallback(
    (qFeature: QualifiedFeature) => {
      setOptions((olderOptions) => {
        const [, isSelected] = olderOptions.get(qFeature.id);
        evt({
          category: CATEGORY.PATIENT_GENERAL,
          action: ACTION.PATIENT_BUTTON_CLICKED,
          label: qFeature.name + "selected=" + !isSelected,
        });
        return new Map([
          ...olderOptions,
          [qFeature.id, [qFeature, !isSelected]],
        ]);
      });
    },
    [setOptions],
  );

  const handleSearchValueAdded = useCallback(
    (qFeature: QualifiedFeatureFragment) => {
      // State full mode - Create a new updated map to re-render the component
      setOptions((olderOptions) => {
        const [, isSelected] = olderOptions.get(qFeature.id) || [null, false];
        evt({
          category: CATEGORY.PATIENT_GENERAL,
          action: ACTION.ADD_DISEASE_FROM_SEARCH,
          label: qFeature.name,
        });
        return new Map([
          ...olderOptions,
          [qFeature.id, [qFeature, !isSelected]],
        ]);
      });
    },
    [],
  );

  // Calculate what options to display in the selection box based on the map entries
  const suggestions = useMemo(
    () => Array.from(options?.values())?.map(([feature, b]) => feature),
    [options],
  );

  return (
    <ClosedMultipleQuestionView
      questionsText={questionText}
      suggestions={suggestions}
      onDone={handleDoneClicked}
      onSelected={handleSuggestionToggle}
      controlledSuggestions={options}
      hideDescription={props.hideDescription}
      basedOnReasoning={false}
      isBackgroundQuestion
      renderChildrenAt={RenderChildrenAt.Bottom}
      miniView={props.miniView}
      skipButtonOverrideText={props.skipButtonOverrideText}
      scrollToAdded
    >
      <PatientPanelSearch
        onSelected={handleSearchValueAdded}
        filteredFeatures={suggested}
        isFirst={options?.size === 0}
      />
    </ClosedMultipleQuestionView>
  );
};
